/** @format */

import ExcelJS from 'exceljs';
import { collectAllLocaleKeys, sortLocaleCodes, type FlatLocalesByCode } from './flattenLocale';

const FIRST_COLUMN_WIDTH_PT = 100;
const OTHER_COLUMN_WIDTH_PT = 200;
const CELL_FONT = {
  name: 'Arial',
  size: 13
};
const EMPTY_CELL_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFFE0' }
};
const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFAAAAAA' }
};
const CELL_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' }
};

function pointsToColumnWidth(pt: number): number {
  return (pt * 96) / (72 * 7);
}

function buildExportFilename(): string {
  const date = new Date().toISOString().slice(0, 10);
  return `AFM-Portal-translations-${date}.xlsx`;
}

function applyColumnWidths(worksheet: ExcelJS.Worksheet, columnCount: number): void {
  worksheet.getColumn(1).width = pointsToColumnWidth(FIRST_COLUMN_WIDTH_PT);
  for (let col = 2; col <= columnCount; col += 1) {
    worksheet.getColumn(col).width = pointsToColumnWidth(OTHER_COLUMN_WIDTH_PT);
  }
}

function applyRowStyles(row: ExcelJS.Row): void {
  const isHeaderRow = row.number === 1;

  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = CELL_FONT;
    cell.border = CELL_BORDER;

    const isFirstColumn = colNumber === 1;
    if (isHeaderRow || isFirstColumn) {
      cell.fill = HEADER_FILL;
      return;
    }

    const value = cell.value == null ? '' : String(cell.value);
    if (value === '') {
      cell.fill = EMPTY_CELL_FILL;
    }
  });
}

function downloadWorkbook(buffer: ExcelJS.Buffer, filename: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export async function exportLocalesToExcel(flatLocales: FlatLocalesByCode): Promise<void> {
  const localeCodes = sortLocaleCodes(Object.keys(flatLocales));
  const keys = collectAllLocaleKeys(flatLocales);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('translations');

  applyColumnWidths(worksheet, localeCodes.length + 1);

  worksheet.views = [
    {
      state: 'frozen',
      xSplit: 1,
      ySplit: 1,
      topLeftCell: 'B2',
      activeCell: 'B2'
    }
  ];

  const headerRow = worksheet.addRow(['key', ...localeCodes]);
  applyRowStyles(headerRow);

  keys.forEach(key => {
    const row = worksheet.addRow([key, ...localeCodes.map(locale => flatLocales[locale][key] ?? '')]);
    applyRowStyles(row);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadWorkbook(buffer, buildExportFilename());
}
